import * as React from 'react';
import { Box, Grid, HStack } from '@chakra-ui/react';
import * as Images from 'pos/Images';
import { Res } from 'core/api';
import * as ThemeManager from './ThemeManager';
import { ActionButton, ActionButtonDefinition, ButtonSurface, NullActionButton } from './ActionButton';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import z from 'zod';
import { SvgIcon } from 'core/ui-next/chakra-components';
import { Close } from 'core/img';
import { LargeDeviceOnly } from './LargeDeviceOnly';

export function MoreActionsGrid({ rows, columns, buttons, surface, criteria }: { surface: ButtonSurface, rows: number, columns: number, buttons: Nullable<ActionButtonDefinition>[], criteria?: string }) {

	// El ancho de columna es el del botón. Antes se leía de la receta de Chakra; ahora el botón es
	// CSS plano y lo publica en --btn-pos-action-width, así que basta con poner en el contenedor
	// las mismas clases de métricas (ver pos-action-button.less) y usar la variable. De esa forma
	// la medida sigue estando declarada en un solo sitio.
	const metricsClassName = [
		ThemeManager.isPhone() ? 'btn-pos-action-metrics-phone' : 'btn-pos-action-metrics-large',
		surface === 'edit-ticket-line-modal' ? 'btn-pos-action-metrics-edit-line' : ''
	].filter(Boolean).join(' ');

	const normalizedCriteria = criteria?.trim().toLowerCase() ?? '';
	const isDimmed = (button: Nullable<ActionButtonDefinition>) =>
		normalizedCriteria.length > 0 && (button === null || !button.title?.toLowerCase().includes(normalizedCriteria));

	return <Grid
		className={metricsClassName}
		templateRows={`repeat(${rows}, 1fr)`}
		templateColumns={`repeat(${columns}, var(--btn-pos-action-width))`}
		gap={2}
		overflow={'auto'}
	>
		{buttons.map((button, index) => {
			const content = button === null ? <NullActionButton key={index} surface={surface} /> : <ActionButton key={index} surface={surface} {...button} />;
			return isDimmed(button)
				? <Box key={index} opacity={0.4} pointerEvents={'none'}>{content}</Box>
				: content;
		})}
	</Grid>;
}

type MoreActionsOverlayProps = {
	buttons: Nullable<ActionButtonDefinition>[];
	rows: number;
	columns: number;
	renderTrigger: (args: { toggle: () => void; isOpen: boolean }) => React.ReactNode;
};

const stateSchema = z.object({
	criteria: z.string()
});

type FormState = z.infer<typeof stateSchema>;


// Esto es un bastante complicado, pero es necesario para que el rendimiento percibido sea mejor en un T11.
//
// En lugar de usar directamente un showModal, que sería lo más sencillo, se mantiene un overlay y se juega
// con su display (none/flex) para no hay que andar montando y desmontando componentes y ahorrar tiempo de
// render.
//
// Para evitar que al montar el botón de otras acciones haya que renderizar todo (que por defecto, no se va a
// mostrar), se hace un render perezoso de los botones basado en dos condiciones:
// - Cuando se muestra el diálogo, hay que renderizarlos.
// - Para evitar que en el primer show se tarde mucho en renderizarlos, se intentan renderizar en segundo plano
//   mediante un requestIdleCallback o un setTimeout, que establecen el isPrewarmed.
//
// Los botones ya renderizados se guardan memoizados para no volver a pagar el precio del render.
//
// Este overlay se puede usar de forma independiente al trigger, el cual recibe a través de un renderProp, para
// poder usar la misma técnica en la pantalla de planos de mesas y en la pantalla principal, porque uno usa un
// SideBarButton, y el otro un ActionButton.
export function MoreActionsOverlay(props: MoreActionsOverlayProps) {

	const [isOpen, setIsOpen] = React.useState(false);
	const [hasOpened, setHasOpened] = React.useState(false);
	const [isPrewarmed, setIsPrewarmed] = React.useState(false);

	React.useEffect(() => {
		const idleCallback = window.requestIdleCallback?.(() => setIsPrewarmed(true));
		const timeoutId = idleCallback === undefined ? window.setTimeout(() => setIsPrewarmed(true), 200) : undefined;

		return () => {
			if (idleCallback !== undefined && window.cancelIdleCallback) {
				window.cancelIdleCallback(idleCallback);
			}
			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
		};
	}, []);

	const toggleMoreOptions = React.useCallback(() => {
		setHasOpened(true);
		setIsOpen(prev => !prev);
	}, []);

	const wrappedButtons = React.useMemo(() => props.buttons.map(btn => {
		return btn === null
			? null
			: {
				...btn,
				action: async () => {
					void btn.action?.();
					setIsOpen(false);
				}
			};
	}), [props.buttons]);


	const [criteria, setCriteria] = React.useState('');

	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input } = useControlsFor<FormState>();


	const initialState: FormState = React.useMemo(() => ({
		criteria: ''
	}), []);


	// Limpia el criteria de busqueda del formulario (el onWatch resetea `criteria` al string vacío).
	const onClear = React.useCallback(() => {
		formRef.current?.setValue('criteria', '');
	}, []);


	// Limpia la busqueda cuando se cierra el overlay.
	React.useEffect(() => {
		if (!isOpen)
			onClear();
	}, [isOpen, onClear]);

	const grid = React.useMemo(() => {
		if (!hasOpened && !isPrewarmed)
			return null;

		return <Box w={'full'} h={'full'}>
			<LargeDeviceOnly>
				<HStack w={'full'} justifyContent={'end'} paddingBottom={'6'}>
					<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ width: '380px' }}>
						<Input
							linkTo="criteria"
							placeholder={Res.Web.OTHER_SEARCH_PLACEHOLDER()}
							onWatch={value => setCriteria(value.trim())}
							startElement={
								<SvgIcon src={Images.Search} width={'4'} height={'4'} />
							}
							endElement={
								<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />
							}
							borderRadius={'4xl'}
							borderColor={'neutral.600'}
							height={'10'}
						/>
					</Form>
				</HStack >
			</LargeDeviceOnly>
			<MoreActionsGrid
				rows={props.rows}
				columns={props.columns}
				buttons={wrappedButtons}
				surface='actions-grid'
				criteria={criteria}
			/>
		</Box >;

	}, [hasOpened, isPrewarmed, props.rows, props.columns, Form, Input, initialState, onClear, wrappedButtons, criteria]);

	if (wrappedButtons.every(b => b === null))
		return null;

	return <Box>
		{props.renderTrigger({ toggle: toggleMoreOptions, isOpen })}
		<Box
			position='fixed'
			inset='0'
			zIndex='modal'
			bg={{ base: 'blackAlpha.600', _dark: 'blackAlpha.800' }}
			display={isOpen ? 'flex' : 'none'}
			alignItems='center'
			justifyContent='center'
			onClick={() => setIsOpen(false)}
		>
			<Box
				padding={'7'}
				maxWidth={'94dvw'}
				overflow={'auto'}
				shadow={'dark-lg'}
				borderRadius={'lg'}
				border={{ base: 'none', _dark: '1px solid var(--chakra-colors-neutral-700)' }}
				bg={{ base: 'neutral.100', _dark: 'neutral.700' }}
				onClick={(event) => event.stopPropagation()}
			>
				{grid}
			</Box>
		</Box>
	</Box>;
}

type MoreActionButtonProps = {
	buttons: Nullable<ActionButtonDefinition>[];
	rows: number;
	columns: number;
	surface: ButtonSurface;
};

export function MoreActionsButton(props: MoreActionButtonProps) {
	return <MoreActionsOverlay
		buttons={props.buttons}
		rows={props.rows}
		columns={props.columns}
		renderTrigger={({ toggle }) => (
			<ActionButton
				title={Res.Web.OTHER_BTN()}
				type='primary'
				surface={props.surface}
				action={async () => toggle()}
				image={Images.MoreOptions}
				intent='more-options'
			/>
		)}
	/>;
}
